import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Trophy, Clock, MousePointer2, Play } from 'lucide-react';
import { Navigation } from '@/components/Navigation';
import { Button } from '@/components/ui/button';
import { AdSlot } from '@/components/ads/AdSlot';
import { useAuth } from '@/hooks/useAuth';
import { useAds } from '@/hooks/useAds';
import { supabase } from '@/integrations/supabase/client';
import { Match, Run, Profile } from '@/types/game';
import { formatTime } from '@/lib/utils';
import { format } from 'date-fns';

interface LeaderboardEntry {
  run: Run;
  profile: Profile;
}

export default function DailyPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { shouldShowAd } = useAds();
  const [dailyMatch, setDailyMatch] = useState<Match | null>(null);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [userBestRun, setUserBestRun] = useState<Run | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchDailyChallenge();
  }, [user]);

  const fetchDailyChallenge = async () => {
    setIsLoading(true);
    try {
      const today = format(new Date(), 'yyyy-MM-dd');
      
      // Fetch today's daily match
      const { data: scheduleData } = await supabase
        .from('daily_schedule')
        .select('match_id, match:matches(*)')
        .eq('day', today)
        .single();

      if (scheduleData?.match) {
        setDailyMatch(scheduleData.match as Match);
        await fetchLeaderboard(scheduleData.match_id);
        if (user) {
          await fetchUserBest(scheduleData.match_id, user.id);
        }
      }
    } catch (err) {
      console.error('Error fetching daily challenge:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchLeaderboard = async (matchId: string) => {
    const { data } = await supabase
      .from('runs')
      .select('*, profile:profiles(*)')
      .eq('match_id', matchId)
      .eq('completed', true)
      .eq('is_public', true)
      .order('duration_ms', { ascending: true })
      .limit(10);

    if (data) {
      setLeaderboard(data.map(r => ({
        run: { ...r, path: r.path as string[] || [] } as Run,
        profile: r.profile as Profile,
      })));
    }
  };

  const fetchUserBest = async (matchId: string, userId: string) => {
    const { data } = await supabase
      .from('runs')
      .select('*')
      .eq('match_id', matchId)
      .eq('user_id', userId)
      .eq('completed', true)
      .order('duration_ms', { ascending: true })
      .limit(1)
      .single();

    if (data) {
      setUserBestRun({ ...data, path: data.path as string[] || [] } as Run);
    }
  };

  const handlePlayDaily = () => {
    if (dailyMatch) {
      navigate(`/play?daily=true&matchId=${dailyMatch.id}`);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="container mx-auto px-4 py-8 text-center">
          <p className="text-muted-foreground">Loading daily challenge...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          {/* Daily Challenge Card */}
          <div className="game-card mb-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center">
                <Calendar className="w-6 h-6 text-accent" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">Daily Challenge</h1>
                <p className="text-muted-foreground">{format(new Date(), 'MMMM d, yyyy')}</p>
              </div>
            </div>

            {dailyMatch ? (
              <>
                <div className="bg-background/50 rounded-lg p-4 mb-6">
                  <p className="text-sm text-muted-foreground mb-2">Today's Route</p>
                  <div className="flex items-center gap-3 text-lg font-semibold">
                    <span>{dailyMatch.start_title}</span>
                    <span className="text-primary">→</span>
                    <span className="text-primary">{dailyMatch.target_title}</span>
                  </div>
                </div>

                {userBestRun && (
                  <div className="bg-primary/10 rounded-lg p-4 mb-6">
                    <p className="text-sm text-muted-foreground mb-2">Your Best Today</p>
                    <div className="flex items-center gap-6">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-accent" />
                        <span className="font-semibold">{formatTime(userBestRun.duration_ms || 0)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <MousePointer2 className="w-4 h-4 text-primary" />
                        <span className="font-semibold">{userBestRun.clicks} clicks</span>
                      </div>
                    </div>
                  </div>
                )}

                <Button onClick={handlePlayDaily} className="w-full gap-2">
                  <Play className="w-5 h-5" />
                  {userBestRun ? 'Play Again' : 'Start Challenge'}
                </Button>
              </>
            ) : (
              <div className="text-center py-8">
                <p className="text-muted-foreground">No daily challenge available today.</p>
                <Button onClick={() => navigate('/play')} variant="outline" className="mt-4">
                  Play Custom Match
                </Button>
              </div>
            )}
          </div>

          {shouldShowAd && (
            <div className="mb-8">
              <AdSlot placement="pre-game" />
            </div>
          )}

          {/* Leaderboard */}
          {leaderboard.length > 0 && (
            <div className="game-card">
              <div className="flex items-center gap-2 mb-4">
                <Trophy className="w-5 h-5 text-primary" />
                <h2 className="text-xl font-bold">Today's Leaderboard</h2>
              </div>
              <div className="space-y-2">
                {leaderboard.map((entry, index) => (
                  <div
                    key={entry.run.id}
                    className={`flex items-center gap-4 p-3 rounded-lg ${
                      index === 0 ? 'bg-primary/20' : 'bg-background/50'
                    }`}
                  >
                    <span className={`w-8 text-center font-bold ${
                      index === 0 ? 'text-primary text-xl' : 'text-muted-foreground'
                    }`}>
                      #{index + 1}
                    </span>
                    <div className="flex-1">
                      <p className="font-semibold">{entry.profile.display_name || entry.profile.username}</p>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3 text-accent" />
                        {formatTime(entry.run.duration_ms || 0)}
                      </span>
                      <span className="flex items-center gap-1">
                        <MousePointer2 className="w-3 h-3" />
                        {entry.run.clicks}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}